/**
 * Runtime loader for vsme micro-frontend bundles (dashboard / reports / diligence).
 *
 * accounting-hub does not source-compile these vsme feature bundles; it injects them at
 * runtime and mounts via their `window.VSME*API` contract (MIG-015). Module-level maps
 * dedupe concurrent loads and survive re-mounts so a bundle script is added at most once.
 */

const loadedBundles = new Set<string>();
const loadingBundles = new Map<string, Promise<void>>();

/**
 * Inject the bundle `src` once and resolve when its `apiKey` global is available.
 * `errorMessage` is supplied by the caller so i18n stays at the call site.
 */
export const loadVsmeBundle = (
  bundle: string,
  src: string,
  apiKey: string,
  errorMessage: string,
): Promise<void> => {
  if (loadedBundles.has(bundle) || apiKey in window) {
    loadedBundles.add(bundle);
    return Promise.resolve();
  }

  const pending = loadingBundles.get(bundle);
  if (pending) {
    return pending;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector(`script[data-vsme-bundle="${bundle}"]`);
    if (existingScript) {
      const handleLoad = () => {
        loadedBundles.add(bundle);
        loadingBundles.delete(bundle);
        resolve();
      };
      const handleError = () => {
        loadingBundles.delete(bundle);
        reject(new Error(errorMessage));
      };
      existingScript.addEventListener('load', handleLoad, { once: true });
      existingScript.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.dataset.vsmeBundle = bundle;
    script.onload = () => {
      loadedBundles.add(bundle);
      loadingBundles.delete(bundle);
      resolve();
    };
    script.onerror = () => {
      loadingBundles.delete(bundle);
      reject(new Error(errorMessage));
    };
    document.body.appendChild(script);
  });

  loadingBundles.set(bundle, promise);
  return promise;
};
